// ============================================================================
// Confirm availability for several lines of ONE order, in one go.
//
// This is the normal way to work the queue, not an optimisation. The planner
// groups demand by (vendor, warehouse, lead time) into one allocation — and so
// one purchase order — per group, but only within a single request. Confirming
// line by line therefore raises a separate PO for every line even when they all
// come from the same supplier on the same day.
//
// So the dialog is built around the common case: pick one source + lead time,
// apply it to every line, submit once. Per-line overrides sit in the table for
// the orders that genuinely split across suppliers.
//
// Partly-answered is normal, and must not be a dead end. An operator opening a
// whole enquiry will often not know one item's lead time yet; blocking the
// submit until every row is filled is what pushed the desk back to confirming
// one line at a time — which is the thing that fragments the POs. So an
// incomplete row is SKIPPED, not an error: it is left off the request and stays
// on the desk for the next pass. Only genuinely invalid input (a qty above what
// is left to source) blocks, because that one cannot be silently dropped
// without losing what the operator typed.
//
// The picklist is a default, not a fence. It offers the variant's preferred
// supplier panel, but the server only requires that the vendor owns the
// warehouse — so typing in the Source select also searches EVERY active
// supplier, under "Other suppliers". A vendor missing from the panel (new
// supplier, one-off buy, panel not yet mapped) used to send the operator to a
// data fix before they could source the line at all.
//
// That wider list is fetched once when the dialog opens and filtered here, in
// the browser: a few hundred suppliers is small enough to hold, and querying
// the server per keystroke made typing lag behind the operator.
// ============================================================================
import { Alert, Button, InputNumber, Modal, Select, Space, Spin, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useState } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import { fmtQty } from "../../format";
import moment from "moment";
import {
  confirmAvailabilityBatch,
  getIncomingPoOptions,
  getSourcingOptions,
  listActiveSuppliers,
} from "../../oms.api";
import {
  ConfirmableLine,
  IncomingPurchaseOrderRow,
  SourcingOptions,
  SourcingSupplier,
  SupplierSearchRow,
} from "../../types";

interface Props {
  open: boolean;
  lines: ConfirmableLine[];
  onClose: () => void;
  onConfirmed: () => void;
}

// One pickable source, flattened from the server's {stock, procure} split so a line needs a single
// dropdown instead of a path toggle plus two selects. The key round-trips the vendor, which the
// stock path deliberately lacks — sending a vendor with an MD warehouse is refused server-side.
interface SourceOption {
  key: string;
  label: string;
  group: string;
  wh_id: number;
  vendor_id?: number;
  // Lowercased vendor + warehouse + city, precomputed once at fetch so filtering a few hundred
  // suppliers on every keystroke is a substring scan and nothing else.
  haystack: string;
}

const flatten = (options: SourcingOptions): SourceOption[] => [
  ...options.stock.map((w) => ({
    key: `stock:${w.id}`,
    label: [w.name || `WH ${w.id}`, w.city].filter(Boolean).join(" · "),
    group: "In our stock (no PO)",
    wh_id: w.id,
    haystack: [w.name, w.city].filter(Boolean).join(" ").toLowerCase(),
  })),
  ...options.procure.flatMap(supplierOptions),
];

// One supplier row → its warehouses as pickable options. Shared by the panel picklist and the
// off-panel search so a searched vendor is indistinguishable from a preferred one once chosen —
// the same `key`, so re-searching never duplicates an already-selected option.
function supplierOptions(s: SourcingSupplier | SupplierSearchRow): SourceOption[] {
  const vendor = s.vendor_name || `Vendor #${s.vendor_id}`;
  // Off-panel hits share ONE group. A group per vendor turned a search into a wall of headers with
  // one row under each, and the vendor's name — the only thing the operator is reading — was in
  // the header, where it truncates. On a search hit the name belongs in the option itself.
  const offPanel = "is_preferred" in s && !s.is_preferred;
  return s.warehouses.map((w) => ({
    key: `vendor:${s.vendor_id}:${w.id}`,
    label: offPanel
      ? [vendor, w.name, w.city].filter(Boolean).join(" · ")
      : [w.name || `WH ${w.id}`, w.city].filter(Boolean).join(" · "),
    group: offPanel ? "Other suppliers" : `Procure — ${vendor}`,
    wh_id: w.id,
    vendor_id: s.vendor_id,
    haystack: [vendor, w.name, w.city].filter(Boolean).join(" ").toLowerCase(),
  }));
}

const toSelectGroups = (opts: SourceOption[]) => {
  const order: string[] = [];
  const byGroup: Record<string, SourceOption[]> = {};
  opts.forEach((o) => {
    if (!byGroup[o.group]) {
      byGroup[o.group] = [];
      order.push(o.group);
    }
    byGroup[o.group].push(o);
  });
  return order.map((label) => ({
    label,
    options: byGroup[label].map((o: SourceOption) => ({ value: o.key, label: o.label })),
  }));
};

interface Draft {
  source?: string; // SourceOption.key
  lead_time?: number;
  qty?: number; // how much of the line to confirm now (partial availability)
  // Optional: the stock-PO line (mirror row id) the goods are expected on. Recorded, not gated.
  incoming_po_item_id?: number | null;
}

// "PO-00042 · 30 incoming · due 12 Nov" — what the operator reads to tell two POs apart.
const poOptionLabel = (r: IncomingPurchaseOrderRow) => {
  const parts = [r.purchase_order_number || `PO line ${r.id}`, `${Number(r.quantity)} incoming`];
  if (r.delivery_date) parts.push(`due ${moment(r.delivery_date).format("DD MMM")}`);
  else parts.push("no date");
  return parts.join(" · ");
};

// How much of a line is still to source. Absent on the drill-down entry point → the whole line.
const remainingOf = (l: ConfirmableLine): number => Number(l.remaining_qty ?? l.ordered_qty);

const BatchConfirmModal: React.FC<Props> = ({ open, lines, onClose, onConfirmed }) => {
  // The variant's preferred panel, per line. Off-panel suppliers are held separately (below) and
  // folded in per line by `optionsFor`, so a search can never disturb the default picklist.
  const [optionsByLine, setOptionsByLine] = useState<Record<number, SourceOption[]>>({});
  // Open stock-PO lines per line (its variant), for the optional Incoming PO picker.
  const [poOptionsByLine, setPoOptionsByLine] = useState<Record<number, IncomingPurchaseOrderRow[]>>({});
  // Every active supplier's warehouses, fetched once per open. The same list serves every line —
  // only `is_preferred` is per-variant, and the panel already carries that — so it is filtered per
  // line by the term typed there, not refetched.
  const [allSuppliers, setAllSuppliers] = useState<SourceOption[]>([]);
  const [terms, setTerms] = useState<Record<number, string>>({});
  // The off-panel option an operator actually PICKED, kept alive independently of the search that
  // surfaced it: hits are replaced on every keystroke, and a selection whose option has vanished
  // renders as a raw key and submits as a crash.
  const [pickedOffPanel, setPickedOffPanel] = useState<Record<number, SourceOption>>({});
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [bulkSource, setBulkSource] = useState<string>();
  const [bulkLeadTime, setBulkLeadTime] = useState<number | null>(null);

  // Options are per-variant, so every line needs its own lookup.
  useEffect(() => {
    if (!open || !lines.length) return;
    let cancelled = false;
    setLoading(true);
    // Seed each line's confirm-qty to its full remaining qty — the common case is "confirm all of
    // it"; the operator only edits qty down for a genuine partial.
    setDrafts(
      Object.fromEntries(
        lines.map((l) => [
          l.id,
          // Carry the cart desk's PO choice into the picker, so confirming keeps it unless changed.
          { qty: remainingOf(l), incoming_po_item_id: l.incoming_po_item_id ?? undefined },
        ])
      )
    );
    setTerms({});
    setPickedOffPanel({});
    setBulkSource(undefined);
    setBulkLeadTime(null);
    setLoadingSuppliers(true);
    listActiveSuppliers().then((rows) => {
      if (cancelled) return;
      setAllSuppliers(rows.flatMap(supplierOptions));
      setLoadingSuppliers(false);
    });
    Promise.all(lines.map((l) => getIncomingPoOptions(l.id))).then((results) => {
      if (cancelled) return;
      const map: Record<number, IncomingPurchaseOrderRow[]> = {};
      results.forEach((rows, i) => {
        map[lines[i].id] = rows ?? [];
      });
      setPoOptionsByLine(map);
    });
    Promise.all(lines.map((l) => getSourcingOptions(l.id))).then((results) => {
      if (cancelled) return;
      const map: Record<number, SourceOption[]> = {};
      results.forEach((res, i) => {
        map[lines[i].id] = res ? flatten(res) : [];
      });
      setOptionsByLine(map);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, lines]);

  // Local, so it lands on the keystroke. Ranked the way a person reads a list: the suppliers whose
  // name STARTS with what was typed, then the rest of the matches — otherwise "Pacific" buries
  // Pacific Panels under everything that merely contains the word.
  const matchesFor = (lineId: number): SourceOption[] => {
    const term = (terms[lineId] ?? "").trim().toLowerCase();
    if (term.length < 2) return []; // one letter matches half the supplier base
    const starts: SourceOption[] = [];
    const contains: SourceOption[] = [];
    allSuppliers.forEach((o) => {
      if (o.haystack.startsWith(term)) starts.push(o);
      else if (o.haystack.includes(term)) contains.push(o);
    });
    return [...starts, ...contains].slice(0, 50);
  };

  // Everything one line's Source select may show: its panel, the off-panel option already picked
  // for it, and the current search's hits — deduped by key, panel first.
  const optionsFor = (lineId: number): SourceOption[] => {
    const panel = optionsByLine[lineId] ?? [];
    const seen = new Set(panel.map((o) => o.key));
    const extra: SourceOption[] = [];
    const add = (o?: SourceOption) => {
      if (o && !seen.has(o.key)) {
        seen.add(o.key);
        extra.push(o);
      }
    };
    add(pickedOffPanel[lineId]);
    matchesFor(lineId).forEach(add);
    return [...panel, ...extra];
  };

  // Only sources every selected line can actually use may be applied to all — offering one that
  // half the lines can't supply would produce a batch the server rejects wholesale.
  const commonSources = useMemo(() => {
    const lists = lines.map((l) => optionsByLine[l.id] ?? []);
    if (!lists.length || lists.some((list) => !list.length)) return [];
    const [first, ...rest] = lists;
    return first.filter((o) => rest.every((list) => list.some((x) => x.key === o.key)));
  }, [lines, optionsByLine]);

  const applyToAll = () => {
    setDrafts((d) => {
      const next = { ...d };
      lines.forEach((l) => {
        next[l.id] = {
          source: bulkSource ?? next[l.id]?.source,
          lead_time: bulkLeadTime ?? next[l.id]?.lead_time,
          qty: next[l.id]?.qty ?? remainingOf(l), // qty is per-line; bulk never touches it
        };
      });
      return next;
    });
  };

  // A row is ready when it carries everything the server needs. Anything else is simply not sent.
  const isReady = (l: ConfirmableLine): boolean => {
    const d = drafts[l.id];
    const remaining = remainingOf(l);
    return !!d?.source && d.lead_time != null && d.qty != null && d.qty > 0 && d.qty <= remaining;
  };

  // Typed-but-wrong, as opposed to not-yet-known: a qty beyond what is left to source. Skipping
  // these would throw away input the operator meant, so they hold the submit.
  const invalidLines = lines.filter((l) => {
    const d = drafts[l.id];
    if (d?.qty != null && (d.qty <= 0 || d.qty > remainingOf(l))) return true;
    return false;
  });

  const readyLines = lines.filter(isReady);
  const skippedLines = lines.filter((l) => !isReady(l) && !invalidLines.includes(l));
  const linesWithoutOptions = lines.filter((l) => (optionsByLine[l.id] ?? []).length === 0);

  const handleOk = async () => {
    const items = readyLines.map((l) => {
      const d = drafts[l.id]!;
      const opt = optionsFor(l.id).find((o) => o.key === d.source)!;
      return {
        line_id: l.id,
        lead_time: d.lead_time!,
        source_wh_id: opt.wh_id,
        source_vendor_id: opt.vendor_id,
        qty: d.qty!, // full remaining unless the operator dialled it down (partial availability)
        // Only sent when the operator touched it or the line already carried one, so a plain
        // confirm never clears a pointer by accident.
        ...(d.incoming_po_item_id !== undefined ? { incoming_po_item_id: d.incoming_po_item_id } : {}),
      };
    });
    setSaving(true);
    const updated = await confirmAvailabilityBatch({ items });
    setSaving(false);
    if (updated) {
      onConfirmed();
      onClose();
    }
  };

  const columns: ColumnsType<ConfirmableLine> = [
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 13 }}>{r.product?.product_name || "—"}</div>
          {r.product?.private_label_name && (
            <div style={{ fontSize: 12, color: "#531dab" }}>
              Private label: {r.product.private_label_name}
            </div>
          )}
          {r.product?.sku && (
            <div style={{ fontSize: 11, color: "#999" }}>SKU {r.product.sku}</div>
          )}
        </div>
      ),
    },
    { title: "Ordered", dataIndex: "ordered_qty", width: 70, render: fmtQty },
    {
      title: "Confirm qty",
      key: "qty",
      width: 130,
      render: (_, r) => {
        const remaining = remainingOf(r);
        const allocated = Number(r.allocated_qty ?? 0);
        return (
          <div>
            <InputNumber
              size="small"
              min={0}
              max={remaining}
              precision={3}
              style={{ width: "100%" }}
              value={drafts[r.id]?.qty}
              onChange={(v) =>
                setDrafts((d) => ({
                  ...d,
                  [r.id]: { ...d[r.id], qty: v == null ? undefined : Number(v) },
                }))
              }
            />
            {allocated > 0 && (
              <div style={{ fontSize: 11, color: "#999" }}>
                {fmtQty(String(allocated))} sourced · {fmtQty(String(remaining))} left
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Source",
      key: "source",
      width: 260,
      render: (_, r) => {
        const opts = optionsFor(r.id);
        const panel = optionsByLine[r.id] ?? [];
        return (
          <>
            <Select
              size="small"
              showSearch
              // The list is remote past the panel, so antd's own filtering must not hide a hit.
              filterOption={false}
              loading={loadingSuppliers}
              style={{ width: "100%" }}
              placeholder={panel.length ? "Where are the goods?" : "Search any supplier…"}
              notFoundContent={
                loadingSuppliers ? <Spin size="small" /> : "Type a supplier name to search all"
              }
              value={drafts[r.id]?.source}
              options={toSelectGroups(opts)}
              onSearch={(term) => setTerms((t) => ({ ...t, [r.id]: term }))}
              onChange={(v) => {
                const chosen = opts.find((o) => o.key === v);
                if (chosen && !panel.some((o) => o.key === v)) {
                  setPickedOffPanel((picked) => ({ ...picked, [r.id]: chosen }));
                }
                setDrafts((d) => ({ ...d, [r.id]: { ...d[r.id], source: v } }));
              }}
            />
            {!panel.length && (
              <Typography.Text type="warning" style={{ fontSize: 11 }}>
                No preferred supplier — search for one
              </Typography.Text>
            )}
          </>
        );
      },
    },
    {
      title: "Incoming PO",
      key: "incoming_po",
      width: 230,
      render: (_, r) => {
        const rows = poOptionsByLine[r.id] ?? [];
        return (
          <Select
            size="small"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder={rows.length ? "Optional · stock PO on its way" : "No open stock PO"}
            disabled={!rows.length && drafts[r.id]?.incoming_po_item_id == null}
            value={drafts[r.id]?.incoming_po_item_id ?? undefined}
            options={rows.map((row) => ({ value: row.id, label: poOptionLabel(row) }))}
            onChange={(v) =>
              setDrafts((d) => ({
                ...d,
                // Cleared by the operator → explicit null, so the server drops the pointer.
                [r.id]: { ...d[r.id], incoming_po_item_id: v == null ? null : Number(v) },
              }))
            }
          />
        );
      },
    },
    {
      title: "Lead time",
      key: "lead_time",
      width: 110,
      render: (_, r) => (
        <InputNumber
          size="small"
          min={0}
          precision={0}
          style={{ width: "100%" }}
          placeholder="days"
          value={drafts[r.id]?.lead_time}
          onChange={(v) =>
            setDrafts((d) => ({
              ...d,
              [r.id]: { ...d[r.id], lead_time: v == null ? undefined : Number(v) },
            }))
          }
        />
      ),
    },
  ];

  const order = lines[0];

  return (
    <Modal
      title={`Confirm availability — ${lines.length} line${lines.length === 1 ? "" : "s"} on SO #${
        order?.sales_order_id ?? ""
      }`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={
        readyLines.length && readyLines.length < lines.length
          ? `Confirm ${readyLines.length} of ${lines.length} lines`
          : "Confirm & allocate"
      }
      okButtonProps={{
        disabled: loading || !readyLines.length || !!invalidLines.length,
      }}
      confirmLoading={saving}
      destroyOnClose
      centered
      width={860}
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        {loading ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Lines sharing a source and lead time become one purchase order"
              description="They are planned together, so the supplier gets a single PO instead of one per line. Confirming lines separately can't do that."
            />

            {!!linesWithoutOptions.length && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${linesWithoutOptions.length} line(s) have no preferred supplier`}
                description="They are neither stocked nor mapped to a supplier with an active warehouse. You can still source them — type in their Source box to search every active supplier — or map a vendor to those variants and come back."
              />
            )}

            <Space style={{ marginBottom: 12 }} wrap>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Apply to all:
              </Typography.Text>
              <Select
                size="small"
                style={{ width: 300 }}
                placeholder={
                  commonSources.length
                    ? "Source available to every line"
                    : "No source is available to every line"
                }
                disabled={!commonSources.length}
                value={bulkSource}
                options={toSelectGroups(commonSources)}
                onChange={setBulkSource}
              />
              <InputNumber
                size="small"
                min={0}
                precision={0}
                placeholder="lead days"
                style={{ width: 110 }}
                value={bulkLeadTime}
                onChange={(v) => setBulkLeadTime(v == null ? null : Number(v))}
              />
              <Button
                size="small"
                onClick={applyToAll}
                disabled={!bulkSource && bulkLeadTime == null}
              >
                Apply
              </Button>
            </Space>

            <Table
              rowKey="id"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={lines}
            />

            {!!invalidLines.length && (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {invalidLines.length} line(s) have a qty above what is left to source — fix or clear
                them to continue.
              </Typography.Text>
            )}
            {!invalidLines.length && !!skippedLines.length && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {skippedLines.length} line(s) are incomplete and will be skipped — they stay on the
                desk, and nothing you have entered on the others is lost.
              </Typography.Text>
            )}
            <div style={{ marginTop: 8 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Confirm less than the full qty to source part of a line now — the rest stays on this
                desk as pending. Leave a line blank if you do not know it yet.
              </Typography.Text>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default BatchConfirmModal;
